import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import { type CampusEvent } from '@chancenwerk/campus-learning-content/entities/CampusEvent.js';
import { ChancenPermission } from '@chancenwerk/db/permissions.js';
import '@chancenwerk/design/components/cr-button.js';
import '@chancenwerk/design/components/cr-card.js';
import '@chancenwerk/design/components/cr-image.js';
import '@chancenwerk/design/components/cr-input.js';
import { ChromiumSnackbar } from '@chancenwerk/design/components/cr-snackbar.js';
import '@chancenwerk/design/components/fonts.js';
import { type ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { DateTime } from 'luxon';
import { CampusRoutes } from '../routes.js';

/**
 * @customElement campus-component-eventbox
 */
@customElement('campus-component-eventbox')
export class CampusComponentEventbox extends ChemistryLitElement {
  @property({ type: Boolean, reflect: true }) editingMode = false;
  @state() public event: RXController<CampusEvent | undefined> = new RXController<CampusEvent | undefined>(
    this,
    undefined,
  );

  @state() size = new WindowSizeController(this);

  @state() private readonly _currentUser = new RXController(this, currentUser<ChancenwerkUser>());

  override render() {
    if (!this.event.value) return nothing;
    return html`
      <cr-flex space="sm" ${css({ cursor: 'pointer' })}>
        <cr-card
          ${css({ height: '100%', border: 'none' })}
          @click=${async () => {
            if (!this.editingMode) {
              if (!this.event.value?.urlToEvent || this.event.value.urlToEvent === '/') return;
              goTo(this.event.value.urlToEvent);
              return;
            }

            if (!this._currentUser.value?.hasPermission(ChancenPermission.campus.EDIT_EVENTS, ANY_CONTEXT))
              return ChromiumSnackbar.show('Keine Berechtigung um Events zu bearbeiten!');

            goTo(`${CampusRoutes.eventAdmin}/edit/${this.event.value?._id}`);
          }}
        >
          <cr-flex horizontal crossaxis-center space="sm">
            <cr-flex space="sm" flex>
              <cr-flex horizontal space-between crossaxis-center space="sm">
                <cr-icon ${css({ color: this.colors.secondary, fontSize: '2rem' })}>today</cr-icon>
                <cr-h3> ${this.event.value.title?.length ? this.event.value.title : 'Kein Titel'} </cr-h3>
                <cr-text> ${this.event.value.startDate?.toLocaleString(DateTime.DATE_SHORT)} </cr-text>
              </cr-flex>
              <cr-flex horizontal space="sm">
                <cr-text> Beschreibung: </cr-text>
                <cr-text>
                  ${this.event.value.description?.length ? this.event.value.description : 'Keine Beschreibung'}
                </cr-text>
              </cr-flex>
              <cr-flex horizontal space="sm">
                <cr-text> Fächer: </cr-text>
                <cr-text> ${this.event.value.subjects.join(', ')} </cr-text>
              </cr-flex>
              <cr-flex horizontal space="sm">
                <cr-text> Jahrgang: </cr-text>
                <cr-text> ${this.event.value.forGrades.join(', ')} </cr-text>
              </cr-flex>
            </cr-flex>
          </cr-flex>
        </cr-card>
      </cr-flex>
    `;
  }
}
